import { isValidElement } from "react";
import type { ReactNode } from "react";

export type ToastId = string | number;
export type ToastVariant = "error" | "info" | "loading" | "success" | "warning";

type ToastContent = ReactNode | Error | Record<string, unknown>;

type ToastAction = {
  label: ReactNode;
  onClick: () => void;
};

export type ToastOptions = {
  action?: ToastAction;
  description?: ReactNode;
  duration?: false | number;
  id?: ToastId;
};

type ToastUpdate = ToastOptions & {
  title?: ToastContent;
  variant?: ToastVariant;
};

export type ToastRecord = {
  action?: ToastAction;
  description?: ReactNode;
  duration: false | number;
  id: ToastId;
  title: ReactNode;
  variant: ToastVariant;
};

const DEFAULT_DURATION = 4_000;
const ERROR_DURATION = 6_000;
const MAX_ERROR_TEXT_LENGTH = 200;
const TOAST_LIMIT = 3;

let nextToastId = 0;
let toastSnapshot: ToastRecord[] = [];
const listeners = new Set<() => void>();

function emitToastChange() {
  listeners.forEach((listener) => listener());
}

export function subscribeToToasts(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getToastSnapshot() {
  return toastSnapshot;
}

export function dismissToast(id?: ToastId) {
  toastSnapshot =
    id === undefined ? [] : toastSnapshot.filter((item) => item.id !== id);
  emitToastChange();
}

function normalizeToastContent(content: ToastContent): ReactNode {
  if (typeof content !== "object" || content === null) return content;
  if (content instanceof Error) {
    const shortMessage = (content as Error & { shortMessage?: unknown })
      .shortMessage;
    return typeof shortMessage === "string" && shortMessage.trim()
      ? shortMessage
      : content.message;
  }
  if (isValidElement(content) || Array.isArray(content))
    return content as ReactNode;

  const record = content as Record<string, unknown>;
  const message = ["shortMessage", "message", "msg", "code"]
    .map((key) => record[key])
    .find((value) => typeof value === "string" || typeof value === "number");

  return message === undefined ? "Notification" : String(message);
}

function limitErrorText(content: ReactNode): ReactNode {
  if (typeof content !== "string") return content;
  const compact = content.replace(/\s+/g, " ").trim();
  return compact.length > MAX_ERROR_TEXT_LENGTH
    ? `${compact.slice(0, MAX_ERROR_TEXT_LENGTH - 1).trimEnd()}…`
    : compact;
}

function showToast(
  variant: ToastVariant,
  content: ToastContent,
  options: ToastOptions = {},
) {
  const id = options.id ?? `ours-toast-${++nextToastId}`;
  const toastRecord: ToastRecord = {
    action: options.action,
    description:
      variant === "error"
        ? limitErrorText(options.description)
        : options.description,
    duration:
      options.duration ??
      (variant === "loading"
        ? false
        : variant === "error"
          ? ERROR_DURATION
          : DEFAULT_DURATION),
    id,
    title:
      variant === "error"
        ? limitErrorText(normalizeToastContent(content))
        : normalizeToastContent(content),
    variant,
  };
  const existingIndex = toastSnapshot.findIndex((item) => item.id === id);

  toastSnapshot =
    existingIndex === -1
      ? [...toastSnapshot, toastRecord].slice(-TOAST_LIMIT)
      : toastSnapshot.map((item) => (item.id === id ? toastRecord : item));
  emitToastChange();
  return id;
}

function updateToast(id: ToastId, update: ToastUpdate) {
  const current = toastSnapshot.find((item) => item.id === id);
  if (!current) return;

  const variant = update.variant ?? current.variant;
  const next: ToastRecord = {
    ...current,
    ...update,
    duration:
      update.duration ??
      (variant === "loading"
        ? false
        : variant === "error"
          ? ERROR_DURATION
          : DEFAULT_DURATION),
    id,
    title:
      update.title === undefined
        ? current.title
        : normalizeToastContent(update.title),
    variant,
  };

  if (variant === "error") {
    next.title = limitErrorText(next.title);
    next.description = limitErrorText(next.description);
  }

  toastSnapshot = toastSnapshot.map((item) => (item.id === id ? next : item));
  emitToastChange();
}

function createToast(content: ToastContent, options?: ToastOptions) {
  return showToast("info", content, options);
}

export const toast = Object.assign(createToast, {
  dismiss: dismissToast,
  dismissAll: () => dismissToast(),
  error: (content: ToastContent, options?: ToastOptions) =>
    showToast("error", content, options),
  failed: (content: ToastContent, options?: ToastOptions) =>
    showToast("error", content, options),
  info: (content: ToastContent, options?: ToastOptions) =>
    showToast("info", content, options),
  loading: (content: ToastContent, options?: ToastOptions) =>
    showToast("loading", content, options),
  promise: async <T>(
    promise: Promise<T> | (() => Promise<T>),
    messages: {
      error: ToastContent | ((error: unknown) => ToastContent);
      loading: ToastContent;
      success: ToastContent | ((value: T) => ToastContent);
    },
    options?: ToastOptions,
  ) => {
    const id = showToast("loading", messages.loading, options);
    try {
      const value = await (typeof promise === "function" ? promise() : promise);
      updateToast(id, {
        ...options,
        title:
          typeof messages.success === "function"
            ? messages.success(value)
            : messages.success,
        variant: "success",
      });
      return value;
    } catch (error) {
      updateToast(id, {
        ...options,
        title:
          typeof messages.error === "function"
            ? messages.error(error)
            : messages.error,
        variant: "error",
      });
      throw error;
    }
  },
  success: (content: ToastContent, options?: ToastOptions) =>
    showToast("success", content, options),
  update: updateToast,
  warn: (content: ToastContent, options?: ToastOptions) =>
    showToast("warning", content, options),
  warning: (content: ToastContent, options?: ToastOptions) =>
    showToast("warning", content, options),
});

export const Toast = toast;
